import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  ArrowLeft,
  Check,
  CircleCheck,
  Copy,
  Earth,
  Loader2,
  Pencil,
  Plus,
  Search,
  SlidersHorizontal,
  X,
} from 'lucide-react';
import { DomainLogo } from '../../components/BrandLogos';
import { useCurrentUser } from '../../hooks/useCurrentUser';
import {
  createAccount,
  listUsers,
  updateAccount,
  type AdminUser,
  type SearchDomain,
  type WhitelistedDomain,
} from '../../services/api';

const STEPS = ['Account Details', 'Domains', 'People', 'Review'];
const DOMAIN_PATTERN =
  /^[a-z0-9](?:[a-z0-9-]*[a-z0-9])?(?:\.[a-z0-9](?:[a-z0-9-]*[a-z0-9])?)+$/i;

type Props = {
  tenant: WhitelistedDomain | null;
  tenants: WhitelistedDomain[];
  searchDomains: SearchDomain[];
  onCancel: () => void;
  onComplete: () => Promise<void> | void;
};

function initials(user: Pick<AdminUser, 'firstName' | 'lastName' | 'email'>) {
  const value = [user.firstName, user.lastName]
    .filter(Boolean)
    .map((part) => part![0])
    .join('');
  return (value || user.email.slice(0, 2)).toUpperCase();
}

function userName(user: Pick<AdminUser, 'firstName' | 'lastName' | 'email'>) {
  return [user.firstName, user.lastName].filter(Boolean).join(' ') || user.email;
}

function roleLabel(role: AdminUser['role']) {
  if (role === 'superadmin') return 'Super Admin';
  if (role === 'admin') return 'Admin';
  return 'Will become Admin';
}

// Mirrors TenantContextInterceptor: superadmins bypass allowedDomains, and an
// admin with a null list is scoped to every account.
function hasGlobalAccess(user: AdminUser) {
  return (
    user.role === 'superadmin' ||
    (user.role === 'admin' && user.allowedDomains === null)
  );
}

function accessScopeLabel(user: AdminUser) {
  if (hasGlobalAccess(user)) return 'All accounts';
  const count = user.allowedDomains?.length ?? 0;
  if (count === 0) return 'No accounts yet';
  return `${count} account${count === 1 ? '' : 's'}`;
}

function Toggle({
  checked,
  onChange,
  label,
  disabled,
}: {
  checked: boolean;
  onChange: (checked: boolean) => void;
  label: string;
  disabled?: boolean;
}) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={checked}
      aria-label={label}
      disabled={disabled}
      onClick={() => onChange(!checked)}
      className={`relative inline-flex h-6 w-11 rounded-full transition-colors ${
        checked ? 'bg-blue-600' : 'bg-gray-300'
      } ${disabled ? 'cursor-not-allowed opacity-60' : ''}`}
    >
      <span
        className={`mt-0.5 h-5 w-5 rounded-full bg-white shadow transition-transform ${
          checked ? 'translate-x-5' : 'translate-x-0.5'
        }`}
      />
    </button>
  );
}

export function AccountWizard({
  tenant,
  tenants,
  searchDomains,
  onCancel,
  onComplete,
}: Props) {
  const isEdit = Boolean(tenant);
  const currentUser = useCurrentUser();
  const activeDomains = useMemo(
    () => searchDomains.filter((item) => item.isActive),
    [searchDomains],
  );
  const initialDomainKeys = useMemo(() => {
    if (!tenant?.config || !Object.keys(tenant.config).length) {
      return activeDomains.map((item) => item.key);
    }
    return activeDomains
      .filter((item) => {
        const key = Object.keys(tenant.config!).find(
          (candidate) => candidate.toLowerCase() === item.key.toLowerCase(),
        );
        return key ? Boolean((tenant.config![key] as any)?.enabled) : false;
      })
      .map((item) => item.key);
  }, [activeDomains, tenant]);

  const [step, setStep] = useState(0);
  const [domain, setDomain] = useState(tenant?.domain ?? '');
  const [name, setName] = useState(tenant?.name ?? '');
  const [showGolden, setShowGolden] = useState(
    tenant?.includeGoldenDataset ?? true,
  );
  const [selectedDomains, setSelectedDomains] =
    useState<string[]>(initialDomainKeys);
  const [showDomainEditor, setShowDomainEditor] = useState(false);
  const [users, setUsers] = useState<AdminUser[]>([]);
  const [selectedPeople, setSelectedPeople] = useState<number[]>([]);
  const [peopleQuery, setPeopleQuery] = useState('');
  const [peopleOpen, setPeopleOpen] = useState(false);
  const [peopleLoading, setPeopleLoading] = useState(true);
  const [loadError, setLoadError] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [submitPhase, setSubmitPhase] = useState(0);
  const [submitError, setSubmitError] = useState('');
  const [created, setCreated] = useState(false);
  const [shareCode, setShareCode] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);
  const searchRef = useRef<HTMLDivElement>(null);
  const domainsInitialized = useRef(activeDomains.length > 0);

  useEffect(() => {
    if (!tenant && !domainsInitialized.current && activeDomains.length > 0) {
      setSelectedDomains(activeDomains.map((item) => item.key));
      domainsInitialized.current = true;
    }
  }, [activeDomains, tenant]);

  useEffect(() => {
    let active = true;
    listUsers({ status: 'active' })
      .then(({ users: rows }) => {
        if (!active) return;
        const eligible = rows.filter(
          (user) =>
            user.isActive && user.email.toLowerCase().endsWith('@turing.com'),
        );
        setUsers(eligible);
        const accessKey = tenant ? tenant.name || tenant.domain : '';
        const initial = eligible
          .filter(
            (user) =>
              hasGlobalAccess(user) ||
              (Boolean(tenant) && (user.allowedDomains ?? []).includes(accessKey)),
          )
          .map((user) => user.id);
        if (currentUser.id != null && !initial.includes(currentUser.id)) {
          initial.push(currentUser.id);
        }
        setSelectedPeople(initial);
      })
      .catch((error) => {
        if (active) {
          setLoadError(
            error?.response?.data?.message || 'Failed to load Turing users',
          );
        }
      })
      .finally(() => {
        if (active) setPeopleLoading(false);
      });
    return () => {
      active = false;
    };
  }, [currentUser.id, tenant]);

  useEffect(() => {
    const close = (event: MouseEvent) => {
      if (!searchRef.current?.contains(event.target as Node)) {
        setPeopleOpen(false);
      }
    };
    document.addEventListener('mousedown', close);
    return () => document.removeEventListener('mousedown', close);
  }, []);

  const normalizedDomain = domain.trim().toLowerCase();
  const normalizedName = name.trim().toLowerCase();
  const domainValid = DOMAIN_PATTERN.test(normalizedDomain);
  const duplicateDomain =
    !isEdit &&
    domainValid &&
    tenants.some((item) => item.domain.toLowerCase() === normalizedDomain);
  const duplicateName =
    normalizedName.length > 0 &&
    tenants.some(
      (item) =>
        item.id !== tenant?.id &&
        item.name?.trim().toLowerCase() === normalizedName,
    );
  const detailsValid =
    domainValid && normalizedName.length > 0 && !duplicateDomain && !duplicateName;

  const selectedUsers = selectedPeople
    .map((id) => users.find((user) => user.id === id))
    .filter((user): user is AdminUser => Boolean(user));
  // Saving grants console access, so a plain `user` is promoted to admin server-side.
  const pendingPromotions = selectedUsers.filter((user) => user.role === 'user');
  const availableUsers = users.filter((user) => {
    if (selectedPeople.includes(user.id)) return false;
    const query = peopleQuery.trim().toLowerCase();
    return (
      !query ||
      user.email.toLowerCase().includes(query) ||
      userName(user).toLowerCase().includes(query)
    );
  });

  const toggleDomain = (key: string) => {
    setSelectedDomains((current) =>
      current.includes(key)
        ? current.filter((item) => item !== key)
        : [...current, key],
    );
  };

  const continueStep = () => {
    if (step === 0 && !detailsValid) return;
    if (step === 1 && selectedDomains.length === 0) return;
    if (step === 2 && currentUser.id == null) {
      setLoadError('Current user details are unavailable. Sign in again.');
      return;
    }
    setStep((current) => Math.min(current + 1, 3));
  };

  const submit = async () => {
    if (currentUser.id == null) {
      setSubmitError('Current user details are unavailable. Sign in again.');
      return;
    }
    setSubmitting(true);
    setSubmitError('');
    setSubmitPhase(0);
    const timer = window.setInterval(
      () => setSubmitPhase((phase) => Math.min(phase + 1, 2)),
      700,
    );
    const payload = {
      name: name.trim(),
      includeGoldenDataset: showGolden,
      enabledSearchDomainKeys: selectedDomains,
      managerUserIds: Array.from(
        new Set([...selectedPeople, currentUser.id]),
      ),
    };
    let completingEdit = false;
    try {
      if (tenant) {
        await updateAccount(tenant.id, payload);
        window.clearInterval(timer);
        setSubmitting(false);
        completingEdit = true;
        await onComplete();
      } else {
        const { shareCode: code } = await createAccount({
          ...payload,
          domain: normalizedDomain,
        });
        setShareCode(code ?? null);
        setSubmitPhase(2);
        setCreated(true);
      }
    } catch (error: any) {
      setSubmitError(
        error?.response?.data?.message ||
          `Failed to ${isEdit ? 'update' : 'create'} account`,
      );
    } finally {
      window.clearInterval(timer);
      if (!completingEdit) setSubmitting(false);
    }
  };

  const closeSuccess = async () => {
    setCreated(false);
    await onComplete();
  };

  const portalLink = shareCode
    ? `${window.location.origin}/?ref=${shareCode}`
    : window.location.origin;

  const copyPortal = async () => {
    try {
      await navigator.clipboard.writeText(portalLink);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 1800);
    } catch {
      setCopied(false);
    }
  };

  const currentStepBody = () => {
    if (step === 0) {
      return (
        <div className="space-y-6 p-6">
          <div>
            <h2 className="text-lg font-semibold text-gray-900">Account Details</h2>
            <p className="mt-1 text-sm text-gray-500">
              Name the account and whitelist the email domain its users will join with.
            </p>
          </div>
          <div className="grid gap-4 md:grid-cols-2">
            <div>
              <label className="mb-1 block text-sm font-medium text-gray-700">
                Email Domain
              </label>
              <input
                value={domain}
                disabled={isEdit}
                onChange={(event) => setDomain(event.target.value)}
                placeholder="e.g. acme.com"
                className="w-full rounded-lg border border-gray-300 px-3 py-2 outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-100 disabled:bg-gray-100 disabled:text-gray-500"
              />
              {domain && !domainValid && (
                <p className="mt-1 text-xs text-red-600">Enter a valid email domain.</p>
              )}
              {duplicateDomain && (
                <p className="mt-1 text-xs text-red-600">
                  Account already exists, including deactivated accounts.
                </p>
              )}
            </div>
            <div>
              <label className="mb-1 block text-sm font-medium text-gray-700">
                Display Name
              </label>
              <input
                value={name}
                onChange={(event) => setName(event.target.value)}
                placeholder="e.g. Acme Corp"
                className="w-full rounded-lg border border-gray-300 px-3 py-2 outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-100"
              />
              {duplicateName && (
                <p className="mt-1 text-xs text-red-600">
                  Display name is already used by another account.
                </p>
              )}
            </div>
          </div>
          {domainValid && (
            <div className="rounded-xl border border-gray-200 p-4">
              <div className="flex items-center gap-3">
                <DomainLogo key={normalizedDomain} domain={normalizedDomain} size="md" />
                <div>
                  <div className="text-sm font-semibold text-gray-900">Account Logo</div>
                  <p className="mt-1 text-sm text-gray-500">
                    Loaded automatically from the email domain.
                  </p>
                </div>
              </div>
            </div>
          )}
          <p className="text-xs leading-relaxed text-gray-500">
            Anyone with an email at this domain can join this account automatically.
          </p>
        </div>
      );
    }

    if (step === 1) {
      return (
        <div className="space-y-6 p-6">
          <div>
            <h2 className="text-lg font-semibold text-gray-900">Talent Domains</h2>
            <p className="mt-1 text-sm text-gray-500">
              Choose what kinds of talent this account can discover.
            </p>
          </div>
          <div className="flex flex-col gap-3">
            <div className="rounded-xl border border-blue-200 bg-blue-50/50 p-5">
              <div className="flex flex-col gap-5 sm:flex-row sm:items-center sm:justify-between">
                <div className="flex items-start gap-4">
                  <span className="flex h-11 w-11 items-center justify-center rounded-xl bg-white text-blue-700 shadow-sm ring-1 ring-blue-100">
                    <Earth className="h-5 w-5" />
                  </span>
                  <div>
                    <div className="flex flex-wrap items-center gap-2">
                      <h3 className="font-semibold text-gray-900">All talent domains</h3>
                      <span className="rounded-full bg-blue-100 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-blue-700">
                        Recommended
                      </span>
                    </div>
                    <p className="mt-1 text-sm text-gray-600">
                      {selectedDomains.length} of {activeDomains.length} active domains selected.
                    </p>
                  </div>
                </div>
                <button
                  type="button"
                  onClick={() => setShowDomainEditor((value) => !value)}
                  className="inline-flex w-fit items-center gap-2 rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm font-semibold text-gray-700 shadow-sm hover:bg-gray-50"
                >
                  <Pencil className="h-4 w-4" />
                  Edit
                </button>
              </div>
              <div className="mt-5 flex items-center justify-between border-t border-blue-100 pt-4">
                <div>
                  <div className="text-sm font-semibold text-gray-900">Show Golden Talent</div>
                  <p className="text-xs text-gray-500">
                    {isEdit
                      ? 'Include Golden Talent in account searches.'
                      : 'Always on for new accounts. Can be turned off later by editing the account.'}
                  </p>
                </div>
                <Toggle
                  checked={showGolden}
                  onChange={setShowGolden}
                  label="Show Golden Talent"
                  disabled={!isEdit}
                />
              </div>
              {/* ponytail: hardcoded on — no state/payload until sync-vs-snapshot is actually built */}
              <div className="mt-5 flex items-center justify-between border-t border-blue-100 pt-4">
                <div>
                  <div className="text-sm font-semibold text-gray-900">Sync Golden Talent</div>
                  <p className="text-xs text-gray-500">
                    This account always sees the latest Golden Talent list. New ones show up
                    automatically, removed ones disappear.
                  </p>
                </div>
                <Toggle checked onChange={() => {}} label="Sync Golden Talent" disabled />
              </div>
            </div>
            {showDomainEditor && (
              <div className="rounded-xl border border-gray-200">
                <div className="flex items-center justify-between gap-3 border-b border-gray-200 px-3 py-2.5">
                  <span className="text-xs font-semibold uppercase tracking-wide text-gray-500">
                    {selectedDomains.length} of {activeDomains.length} selected
                  </span>
                  <div className="flex items-center gap-2">
                    <button
                      type="button"
                      onClick={() => setSelectedDomains(activeDomains.map((item) => item.key))}
                      disabled={selectedDomains.length === activeDomains.length}
                      className="rounded-lg border border-gray-300 bg-white px-2.5 py-1.5 text-xs font-semibold text-gray-700 hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-50"
                    >
                      Select all
                    </button>
                    <button
                      type="button"
                      onClick={() => setSelectedDomains([])}
                      disabled={selectedDomains.length === 0}
                      className="rounded-lg border border-gray-300 bg-white px-2.5 py-1.5 text-xs font-semibold text-gray-700 hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-50"
                    >
                      Deselect all
                    </button>
                  </div>
                </div>
                <div className="grid max-h-[360px] gap-2 overflow-y-auto p-3 sm:grid-cols-2">
                {activeDomains.map((item) => {
                  const checked = selectedDomains.includes(item.key);
                  return (
                    <button
                      key={item.id}
                      type="button"
                      onClick={() => toggleDomain(item.key)}
                      className={`flex items-center justify-between rounded-lg border p-3 text-left ${
                        checked
                          ? 'border-blue-200 bg-blue-50'
                          : 'border-gray-200 bg-white hover:bg-gray-50'
                      }`}
                    >
                      <span>
                        <span className="block text-sm font-semibold text-gray-900">
                          {item.label}
                        </span>
                        <span className="block text-xs text-gray-500">{item.key}</span>
                      </span>
                      <span
                        className={`flex h-5 w-5 items-center justify-center rounded border ${
                          checked
                            ? 'border-blue-600 bg-blue-600 text-white'
                            : 'border-gray-300 bg-white'
                        }`}
                      >
                        {checked && <Check className="h-3.5 w-3.5" />}
                      </span>
                    </button>
                  );
                })}
                </div>
              </div>
            )}
            {selectedDomains.length === 0 && (
              <p className="text-sm text-red-600">Select at least one talent domain.</p>
            )}
            <div
              aria-disabled="true"
              className="flex min-h-[88px] flex-col gap-5 rounded-xl border border-gray-200 bg-gray-50 p-5 sm:flex-row sm:items-center sm:justify-between"
            >
              <div className="flex items-start gap-4">
                <span className="flex h-11 w-11 items-center justify-center rounded-xl bg-white text-gray-400 shadow-sm ring-1 ring-gray-200">
                  <SlidersHorizontal className="h-5 w-5" />
                </span>
                <div>
                  <h3 className="font-semibold text-gray-500">Custom Talent Pool</h3>
                  <p className="mt-1 text-sm text-gray-400">
                    Advanced profile rules, domain-specific filters, and custom ranking.
                  </p>
                </div>
              </div>
              <span className="w-fit rounded-full bg-gray-200 px-2.5 py-1 text-[10px] font-semibold uppercase tracking-wide text-gray-600">
                Coming soon
              </span>
            </div>
          </div>
        </div>
      );
    }

    if (step === 2) {
      return (
        <div className="space-y-6 p-6">
          <div>
            <h2 className="text-lg font-semibold text-gray-900">People</h2>
            <p className="mt-1 text-sm text-gray-500">
              Search and select Turing users who can manage this account.
            </p>
          </div>
          {loadError && (
            <div className="rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-700">
              {loadError}
            </div>
          )}
          <div ref={searchRef} className="relative max-w-xl">
            <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
            <input
              type="search"
              value={peopleQuery}
              onFocus={() => setPeopleOpen(true)}
              onChange={(event) => {
                setPeopleQuery(event.target.value);
                setPeopleOpen(true);
              }}
              placeholder="Search Turing users…"
              role="combobox"
              aria-expanded={peopleOpen}
              aria-controls="account-people-options"
              className="w-full rounded-lg border border-gray-300 bg-white py-2.5 pl-9 pr-3 text-sm outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-100"
            />
            {peopleOpen && (
              <div
                id="account-people-options"
                role="listbox"
                className="absolute left-0 right-0 top-full z-30 mt-2 max-h-72 overflow-y-auto rounded-xl border border-gray-200 bg-white p-1.5 shadow-xl"
              >
                {peopleLoading ? (
                  <div className="flex justify-center p-4">
                    <Loader2 className="h-5 w-5 animate-spin text-blue-600" />
                  </div>
                ) : availableUsers.length ? (
                  availableUsers.map((user) => (
                    <button
                      key={user.id}
                      type="button"
                      role="option"
                      aria-selected="false"
                      onClick={() => {
                        setSelectedPeople((current) => [...current, user.id]);
                        setPeopleQuery('');
                        setPeopleOpen(false);
                      }}
                      className="flex w-full items-center gap-3 rounded-lg px-3 py-2.5 text-left hover:bg-gray-50"
                    >
                      <span className="flex h-9 w-9 items-center justify-center rounded-full bg-gray-100 text-xs font-semibold text-gray-600">
                        {initials(user)}
                      </span>
                      <span className="min-w-0 flex-1">
                        <span className="block truncate text-sm font-semibold text-gray-900">
                          {userName(user)}
                        </span>
                        <span className="block truncate text-xs text-gray-500">
                          {user.email} · {roleLabel(user.role)} · {accessScopeLabel(user)}
                        </span>
                      </span>
                      <Plus className="h-4 w-4 text-gray-400" />
                    </button>
                  ))
                ) : (
                  <p className="p-3 text-sm text-gray-500">No matching Turing users.</p>
                )}
              </div>
            )}
          </div>
          <div>
            <div className="mb-3 flex items-center justify-between gap-3">
              <div>
                <h3 className="text-sm font-semibold text-gray-900">Account managers</h3>
                <p className="mt-0.5 text-xs text-gray-500">
                  Admins with access to all accounts are always included and cannot be removed here.
                </p>
              </div>
              <span className="text-xs font-medium text-gray-500">
                {selectedPeople.length} selected
              </span>
            </div>
            <div className="grid max-h-80 gap-3 overflow-y-auto pr-1 sm:grid-cols-2">
              {selectedUsers.map((user) => {
                const isCurrent = user.id === currentUser.id;
                const isGlobal = hasGlobalAccess(user);
                const locked = isCurrent || isGlobal;
                return (
                  <div
                    key={user.id}
                    className="flex items-center gap-3 rounded-xl border border-blue-200 bg-blue-50 p-4"
                  >
                    <span className="flex h-10 w-10 items-center justify-center rounded-full bg-blue-600 text-sm font-semibold text-white">
                      {initials(user)}
                    </span>
                    <span className="min-w-0 flex-1">
                      <span className="flex items-center gap-2">
                        <span className="truncate text-sm font-semibold text-gray-900">
                          {isCurrent ? 'You' : userName(user)}
                        </span>
                        {isCurrent && (
                          <span className="rounded-full bg-blue-100 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-blue-700">
                            Current user
                          </span>
                        )}
                        {isGlobal && (
                          <span className="rounded-full bg-gray-100 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-gray-600">
                            All accounts
                          </span>
                        )}
                      </span>
                      <span className="block truncate text-xs text-gray-500">
                        {user.email} · {roleLabel(user.role)} · {accessScopeLabel(user)}
                      </span>
                    </span>
                    {locked ? (
                      <Check className="h-4 w-4 text-blue-600" />
                    ) : (
                      <button
                        type="button"
                        aria-label={`Remove ${userName(user)}`}
                        onClick={() =>
                          setSelectedPeople((current) =>
                            current.filter((id) => id !== user.id),
                          )
                        }
                        className="rounded p-1 text-gray-400 hover:bg-white hover:text-red-600"
                      >
                        <X className="h-4 w-4" />
                      </button>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      );
    }

    return (
      <div className="space-y-5 p-6">
        <div>
          <h2 className="text-lg font-semibold text-gray-900">Review</h2>
          <p className="mt-1 text-sm text-gray-500">
            Confirm who can manage this account and which talent domains it includes.
          </p>
        </div>
        {pendingPromotions.length > 0 && (
          <div className="rounded-lg border border-amber-200 bg-amber-50 p-4">
            <div className="text-sm font-semibold text-amber-900">
              {pendingPromotions.length} {pendingPromotions.length === 1 ? 'person' : 'people'} will be promoted to Admin
            </div>
            <p className="mt-1 text-xs leading-5 text-amber-800">
              Accounts can only be managed from the admin console, so saving grants these
              users admin access scoped to this account. This is not reversed if you later
              remove them as managers.
            </p>
            <ul className="mt-2 max-h-28 space-y-1 overflow-y-auto pr-1">
              {pendingPromotions.map((user) => (
                <li key={user.id} className="text-xs font-medium text-amber-900">
                  {userName(user)} · {user.email}
                </li>
              ))}
            </ul>
          </div>
        )}
        <div className="grid gap-4 md:grid-cols-2">
          <div className="rounded-lg border border-gray-200 p-4">
            <div className="text-xs font-semibold uppercase tracking-wide text-gray-500">
              Account
            </div>
            <div className="mt-3 flex gap-3">
              <DomainLogo key={normalizedDomain} domain={normalizedDomain} size="sm" />
              <div>
                <div className="font-semibold text-gray-900">{name.trim()}</div>
                <div className="mt-1 text-sm text-gray-500">{normalizedDomain}</div>
              </div>
            </div>
            <p className="mt-4 border-t border-gray-100 pt-3 text-xs leading-5 text-gray-500">
              Anyone signing in with an{' '}
              <span className="font-medium text-gray-700">@{normalizedDomain}</span>{' '}
              email can join this account.
            </p>
          </div>
          <div className="rounded-lg border border-gray-200 p-4">
            <div className="text-xs font-semibold uppercase tracking-wide text-gray-500">
              Turing access
              <span className="ml-1 font-normal normal-case tracking-normal text-gray-400">
                ({selectedUsers.length})
              </span>
            </div>
            <div className="mt-3 max-h-48 space-y-2 overflow-y-auto pr-1">
              {selectedUsers.map((user) => (
                <div key={user.id} className="flex items-center gap-2 text-sm">
                  <span className="flex h-7 w-7 items-center justify-center rounded-full bg-blue-50 text-[10px] font-semibold text-blue-700">
                    {initials(user)}
                  </span>
                  <span className="min-w-0 flex-1 truncate font-medium text-gray-900">
                    {user.id === currentUser.id ? 'You' : userName(user)}
                  </span>
                  <span className="shrink-0 text-xs text-gray-400">
                    {roleLabel(user.role)} · {accessScopeLabel(user)}
                  </span>
                </div>
              ))}
            </div>
          </div>
          <div className="rounded-lg border border-gray-200 p-4 md:col-span-2">
            <div className="flex items-center gap-2">
              <div className="text-xs font-semibold uppercase tracking-wide text-gray-500">
                Golden Talent access
              </div>
              <span
                className={`rounded-full px-2 py-0.5 text-[10px] font-semibold ${
                  showGolden
                    ? 'bg-green-50 text-green-700'
                    : 'bg-gray-100 text-gray-600'
                }`}
              >
                Show Golden Talent: {showGolden ? 'On' : 'Off'}
              </span>
            </div>
            <p className="mt-1 text-sm text-gray-600">
              This account can discover talent across selected domains.
            </p>
            <div className="mt-4 rounded-lg border border-gray-100 bg-gray-50 p-3">
              <div className="mb-2 flex items-center justify-between gap-3">
                <span className="text-xs font-semibold uppercase tracking-wide text-gray-500">
                  Selected domains
                </span>
                <span className="text-xs font-medium text-gray-500">
                  {selectedDomains.length} selected
                </span>
              </div>
              <div className="flex flex-wrap gap-1.5">
                {selectedDomains.map((key) => (
                  <span
                    key={key}
                    className="rounded-md bg-white px-2 py-1 font-mono text-xs font-medium text-blue-700 ring-1 ring-inset ring-blue-100"
                  >
                    {key}
                  </span>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  };

  return (
    <>
      <div className="animate-in fade-in mx-auto max-w-[1400px] space-y-6 p-8 duration-300">
        <button
          type="button"
          onClick={onCancel}
          className="flex items-center gap-2 text-sm font-medium text-gray-500 hover:text-gray-900"
        >
          <ArrowLeft className="h-4 w-4" />
          Back to Accounts
        </button>
        <div>
          <h1 className="text-2xl font-semibold text-gray-900">
            {isEdit ? 'Edit Account' : 'Add Account'}
          </h1>
          <p className="mt-1 text-gray-500">
            Configure account details, internal access, and Golden Talent domains.
          </p>
        </div>
        <div className="grid w-full grid-cols-2 gap-2 rounded-xl border border-gray-200 bg-white p-2 shadow-sm md:grid-cols-4">
          {STEPS.map((label, index) => {
            const complete = index < step;
            const current = index === step;
            return (
              <button
                key={label}
                type="button"
                disabled={index > step}
                onClick={() => complete && setStep(index)}
                className={`flex min-w-0 items-center justify-center gap-2 rounded-lg px-3 py-3 text-sm font-medium transition-colors ${
                  current
                    ? 'cursor-default bg-blue-50 text-blue-700'
                    : complete
                      ? 'cursor-pointer text-green-700 hover:bg-gray-50'
                      : 'cursor-default text-gray-500'
                }`}
              >
                <span
                  className={`flex h-6 w-6 items-center justify-center rounded-full text-xs ${
                    current
                      ? 'bg-blue-600 text-white'
                      : complete
                        ? 'bg-green-100 text-green-700'
                        : 'bg-gray-100 text-gray-500'
                  }`}
                >
                  {complete ? <Check className="h-3.5 w-3.5" /> : index + 1}
                </span>
                <span className="truncate">{label}</span>
              </button>
            );
          })}
        </div>
        <div className="overflow-hidden rounded-xl border border-gray-200 bg-white shadow-sm">
          {currentStepBody()}
          <div className="flex items-center justify-between border-t border-gray-200 bg-gray-50 px-6 py-4">
            <button
              type="button"
              onClick={() => (step === 0 ? onCancel() : setStep(step - 1))}
              className="rounded-lg border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
            >
              {step === 0 ? 'Cancel' : 'Back'}
            </button>
            <button
              type="button"
              onClick={step === 3 ? submit : continueStep}
              disabled={
                (step === 0 && !detailsValid) ||
                (step === 1 && selectedDomains.length === 0) ||
                (step === 2 && (peopleLoading || currentUser.id == null)) ||
                submitting
              }
              className="rounded-lg bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-700 disabled:cursor-not-allowed disabled:opacity-50"
            >
              {step === 3
                ? isEdit
                  ? 'Save changes'
                  : 'Create Account'
                : 'Continue'}
            </button>
          </div>
        </div>
      </div>

      {(submitting || submitError) && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/45 p-4">
          <div className="w-full max-w-md rounded-2xl bg-white p-6 shadow-2xl">
            <h2 className="text-lg font-semibold text-gray-900">
              {submitError
                ? `Could not ${isEdit ? 'update' : 'create'} account`
                : isEdit
                  ? 'Updating account'
                  : 'Creating account'}
            </h2>
            {submitError ? (
              <>
                <p className="mt-2 text-sm text-red-600">{submitError}</p>
                <div className="mt-5 flex justify-end gap-2">
                  <button
                    type="button"
                    onClick={() => setSubmitError('')}
                    className="rounded-lg border border-gray-300 px-3 py-2 text-sm font-medium text-gray-700"
                  >
                    Back
                  </button>
                  <button
                    type="button"
                    onClick={submit}
                    className="rounded-lg bg-blue-600 px-3 py-2 text-sm font-medium text-white"
                  >
                    Retry
                  </button>
                </div>
              </>
            ) : (
              <div className="mt-5 space-y-3">
                {[
                  isEdit ? 'Updating account' : 'Creating account',
                  'Saving talent domains',
                  'Updating user access',
                ].map((label, index) => (
                  <div key={label} className="flex items-center gap-3 text-sm">
                    <span
                      className={`flex h-6 w-6 items-center justify-center rounded-full ${
                        index < submitPhase
                          ? 'bg-green-100 text-green-700'
                          : index === submitPhase
                            ? 'bg-blue-100 text-blue-700'
                            : 'bg-gray-100 text-gray-400'
                      }`}
                    >
                      {index < submitPhase ? (
                        <Check className="h-3.5 w-3.5" />
                      ) : index === submitPhase ? (
                        <Loader2 className="h-3.5 w-3.5 animate-spin" />
                      ) : (
                        index + 1
                      )}
                    </span>
                    <span className={index <= submitPhase ? 'text-gray-900' : 'text-gray-400'}>
                      {label}
                    </span>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}

      {created && (
        <div className="animate-in fade-in fixed inset-0 z-50 flex items-center justify-center bg-black/45 p-4 duration-200">
          <div className="animate-in zoom-in-95 w-full max-w-xl overflow-hidden rounded-2xl bg-white shadow-2xl duration-200">
            <div className="relative px-6 pb-5 pt-7 text-center">
              <button
                type="button"
                aria-label="Close account created confirmation"
                onClick={closeSuccess}
                className="absolute right-4 top-4 rounded-lg p-2 text-gray-400 hover:bg-gray-100 hover:text-gray-700"
              >
                <X className="h-5 w-5" />
              </button>
              <span className="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-green-100 text-green-700">
                <CircleCheck className="h-6 w-6" />
              </span>
              <h2 className="mt-4 text-xl font-semibold text-gray-900">Account created</h2>
              <p className="mt-1 text-sm text-gray-500">
                {name.trim()} can now discover talent across selected domains.
              </p>
            </div>
            <div className="space-y-4 border-t border-gray-100 px-6 py-5">
              <div className="grid gap-3 sm:grid-cols-2">
                <div className="rounded-xl border border-gray-200 p-3">
                  <div className="text-[10px] font-semibold uppercase tracking-wide text-gray-400">
                    Talent access
                  </div>
                  <div className="mt-1 text-sm font-semibold text-gray-900">
                    {selectedDomains.length} selected domains
                  </div>
                </div>
                <div className="rounded-xl border border-gray-200 p-3">
                  <div className="text-[10px] font-semibold uppercase tracking-wide text-gray-400">
                    Golden Talent
                  </div>
                  <div className="mt-1 text-sm font-semibold text-gray-900">
                    {showGolden ? 'Shown' : 'Hidden'}
                  </div>
                </div>
              </div>
              <div className="rounded-xl border border-gray-200 bg-gray-50 p-3">
                <label className="text-[10px] font-semibold uppercase tracking-wide text-gray-500">
                  Customer portal link
                </label>
                <div className="mt-2 flex gap-2">
                  <input
                    readOnly
                    className="min-w-0 flex-1 rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm text-gray-700"
                    value={portalLink}
                  />
                  <button
                    type="button"
                    onClick={copyPortal}
                    className="inline-flex items-center gap-2 rounded-lg bg-blue-600 px-3 py-2 text-sm font-semibold text-white hover:bg-blue-700"
                  >
                    <Copy className="h-4 w-4" />
                    {copied ? 'Copied' : 'Copy link'}
                  </button>
                </div>
                <p className="mt-2 text-xs leading-5 text-gray-500">
                  Anyone signing in with an{' '}
                  <span className="font-medium text-gray-700">@{normalizedDomain}</span>{' '}
                  email can join this account.
                </p>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
